export const TRANSFER_SCAN_BROADCAST_EVENT = 'transfer_scan';
export const TRANSFER_LINE_CHANGED_BROADCAST_EVENT = 'transfer_line_changed';

export interface TransferLineChangeBroadcast {
  readonly transferRequestId: string;
  readonly lineId: string;
  readonly eventId: string;
  readonly line: {
    readonly sku: string;
    readonly quantity: number;
    readonly requestedQuantity?: number;
    readonly productName: string;
    readonly displayName: string;
  } | null;
  readonly event: {
    readonly id: string;
    readonly lineId: string;
    readonly eventType: string;
    readonly sku: string | null;
    readonly previousSku: string | null;
    readonly newSku: string | null;
    readonly previousQuantity: number | null;
    readonly newQuantity: number | null;
    readonly actorName: string;
    readonly createdAt: string;
  };
}

export const TRANSFER_PICKS_REALTIME_TOPIC = 'inventory-transfer-picks';

export function transferScanRealtimeTopic(transferRequestId: string): string {
  return `inventory-transfer-scans-${transferRequestId}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readId(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * Read a transfer scan from a Supabase Realtime callback.
 * Postgres inserts use `{ new: { line_id, transfer_request_id } }`.
 * Broadcasts use `{ payload: { lineId, transferRequestId } }`.
 */
export function parseTransferScanInsert(
  raw: unknown,
): { readonly transferRequestId: string; readonly lineId: string } | null {
  if (!isRecord(raw)) return null;

  const body = isRecord(raw.payload) ? raw.payload : raw;
  const row = isRecord(body.new) ? body.new : body;
  const lineId = readId(row.line_id) ?? readId(row.lineId);
  const transferRequestId = readId(row.transfer_request_id) ?? readId(row.transferRequestId);
  if (!lineId || !transferRequestId) return null;

  return { transferRequestId, lineId };
}

function readNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function readNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Read a line-change broadcast. A null line means the pull quantity is now zero.
 */
function lineChangeBody(raw: Record<string, unknown>): Record<string, unknown> {
  const payload = isRecord(raw.payload) ? raw.payload : raw;
  if (typeof payload.event === 'string' && isRecord(payload.payload)) {
    return payload.payload;
  }
  return payload;
}

export function parseTransferLineEventInsert(raw: unknown): { readonly lineId: string; readonly eventId: string } | null {
  if (!isRecord(raw)) return null;
  const body = isRecord(raw.payload) ? raw.payload : raw;
  const row = isRecord(body.new) ? body.new : body;
  const lineId = readId(row.line_id) ?? readId(row.lineId);
  const eventId = readId(row.id);
  if (!lineId || !eventId) return null;
  return { lineId, eventId };
}

export function parseTransferLineChange(raw: unknown): TransferLineChangeBroadcast | null {
  if (!isRecord(raw)) return null;
  const body = lineChangeBody(raw);
  const transferRequestId = readId(body.transferRequestId);
  const lineId = readId(body.lineId);
  const eventId = readId(body.eventId);
  const event = isRecord(body.event) ? body.event : null;
  if (!transferRequestId || !lineId || !eventId || !event) return null;
  const eventLineId = readId(event.lineId);
  const eventType = readId(event.eventType);
  const createdAt = readId(event.createdAt);
  const parsedEventId = readId(event.id);
  const actorName = readId(event.actorName);
  if (!eventLineId || !eventType || !createdAt || !parsedEventId || !actorName) return null;
  if (parsedEventId !== eventId || eventLineId !== lineId) return null;

  let line: TransferLineChangeBroadcast['line'] = null;
  if (body.line != null) {
    if (!isRecord(body.line)) return null;
    const sku = readId(body.line.sku);
    const quantity = readNullableNumber(body.line.quantity);
    if (!sku || quantity == null) return null;
    const requestedQuantity = readNullableNumber(body.line.requestedQuantity);
    line = {
      sku,
      quantity,
      ...(requestedQuantity == null ? {} : { requestedQuantity }),
      productName: typeof body.line.productName === 'string' ? body.line.productName : '',
      displayName: typeof body.line.displayName === 'string' ? body.line.displayName : sku,
    };
  }

  return {
    transferRequestId,
    lineId,
    eventId,
    line,
    event: {
      id: parsedEventId,
      lineId: eventLineId,
      eventType,
      sku: readNullableString(event.sku),
      previousSku: readNullableString(event.previousSku),
      newSku: readNullableString(event.newSku),
      previousQuantity: readNullableNumber(event.previousQuantity),
      newQuantity: readNullableNumber(event.newQuantity),
      actorName,
      createdAt,
    },
  };
}
